import React from 'react';
import { CheckCircle, Shield, Award, FileCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { certifications } from '../mock';

const Quality = () => {
  return (
    <div className="bg-gray-950 text-white">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-b from-gray-900 to-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Quality & Certifications</h1>
            <p className="!text-xl text-gray-400">Our AS9100 Rev. D – Quality system ensures consistent precision, repeatability, and reliability across every machined component we deliver.
            </p>
          </div>
        </div>
      </section>

      {/* Quality Commitment Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Our Quality Commitment</h2>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                Aether Machining Inc. operates under a robust quality management system designed to meet 
                the demanding requirements of aerospace and high-precision industries.
              </p>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                From first article inspection to final delivery, quality is built into every step of our process. 
                We maintain strict process controls, comprehensive documentation, and continuous improvement programs 
                to ensure every component meets or exceeds specifications.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Documented Processes</h3>
                    <p className="text-gray-400">Comprehensive work instructions and quality procedures</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Full Traceability</h3>
                    <p className="text-gray-400">Complete material and process traceability systems</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Continuous Improvement</h3>
                    <p className="text-gray-400">Ongoing corrective action and quality enhancement programs</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_aerospace-cnc/artifacts/wsjrxpti_Keyence%20XM.jpg"
                alt="Quality Control"
                className="rounded-lg shadow-2xl" />

            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Certifications & Standards</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Our certifications demonstrate our commitment to aerospace-grade quality and precision manufacturing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {certifications.map((cert, index) =>
            <Card key={index} className="bg-gray-950 border-gray-800 p-8 text-center hover:border-red-600 transition-all duration-300">
                <div className="w-20 h-20 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Shield className="h-10 w-10 text-red-500" />
                </div>
                <h3 className="!font-semibold !text-2xl mb-4 !text-[#E5E6E5]">{cert.name}</h3>
                <p className="text-gray-400">{cert.description}</p>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Quality Process Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Quality Process</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Every component goes through rigorous quality checks at multiple stages
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="bg-gray-900 border-gray-800 p-6 text-center">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileCheck className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="!font-semibold !text-xl mb-3 !text-[#E5E6E5]">1. First Article Inspection</h3>
              <p className="text-gray-400">Comprehensive inspection of initial production samples</p>
            </Card>

            <Card className="bg-gray-900 border-gray-800 p-6 text-center">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="!font-semibold !text-xl mb-3 !text-[#E5E6E5]">2. In-Process Inspection</h3>
              <p className="text-gray-400">Real-time quality checks during manufacturing</p>
            </Card>

            <Card className="bg-gray-900 border-gray-800 p-6 text-center">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="!font-semibold !text-xl mb-3 !text-[#E5E6E5]">3. Final Inspection</h3>
              <p className="text-gray-400">Complete dimensional and visual inspection</p>
            </Card>

            <Card className="bg-gray-900 border-gray-800 p-6 text-center">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="!font-semibold !text-xl mb-3 !text-[#E5E6E5]">4. Documentation</h3>
              <p className="text-gray-400">Complete inspection reports and certifications</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="bg-gray-950 border-gray-800 p-8">
              <h3 className="!font-semibold !text-2xl mb-4 !text-[#E5E6E5]">Inspection Capabilities</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">Coordinate Measuring Machine (CMM) inspection</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">Optical comparators and measurement systems</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">Surface finish analysis</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">Material certifications and test reports</span>
                </li>
              </ul>
            </Card>

            <Card className="bg-gray-950 border-gray-800 p-8">
              <h3 className="!font-semibold !text-2xl mb-4 !text-[#E5E6E5]">Quality Documentation</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">First Article Inspection Reports (FAIR)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">Certificate of Conformance (C of C)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">Material test reports and certifications</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">Complete traceability documentation</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-900/20 to-gray-900/20 border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Experience Quality Excellence</h2>
          <p className="text-xl text-gray-300 mb-8">
            Partner with a precision machining company that puts quality first. Request a quote today.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link to="/contact">
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-md font-semibold transition-all duration-300">
                Request a Quote
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-gray-600 text-white hover:bg-gray-800 px-8 py-3 rounded-md font-semibold">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default Quality;